import { StatCardResultDto } from "api/interfaces/planInterface.interface";
import { formatNumber } from "utils/functions";
import * as s from "./BreakdownStyled";

export const STORAGE_BREAKDOWN_TITLE = "Storage calculation breakdown";
export const STORAGE_BREAKDOWN_SUBTITLE =
  "Step-by-step math behind how pro-rated storage is calculated for each organization.";

type Props = {
  summary: StatCardResultDto;
  sectionTitle?: string;
};

export function useStorageBreakdown(summary: StatCardResultDto) {
  const sumStorage = summary.activeStorage?.storage ?? 0;
  const storagePrice = summary.activeStorage?.storagePrice ?? 0;
  const storageCost = sumStorage * storagePrice;

  return { sumStorage, storagePrice, storageCost };
}

export default function StorageBreakdownContent({
  summary,
  sectionTitle = "Storage usage",
}: Props): JSX.Element {
  const { sumStorage, storagePrice, storageCost } =
    useStorageBreakdown(summary);

  return (
    <s.Wrapper>
      <s.Section>
        <div className="section-title">{sectionTitle}</div>
        <div className="section-desc">
          For each organization: allocated TB × active days ÷ days in month =
          prorated TB-months. All organizations are summed, then priced per
          TB-month. Days after today are projected at the current allocation so
          the full billing period is covered.
        </div>
      </s.Section>

      <s.Box>
        <s.Row className="highlight">
          <span className="label">Total TB-months</span>
          <span className="value">{sumStorage.toFixed(2)}</span>
        </s.Row>
        <s.Divider />
        <s.Row>
          <span className="label">Storage cost</span>
          <span className="value">
            {sumStorage.toFixed(2)} × ${formatNumber(storagePrice)} = $
            {formatNumber(storageCost, { fractionDigits: 2 })}
          </span>
        </s.Row>
      </s.Box>
    </s.Wrapper>
  );
}
